import React from "react";
import { ArrayInput, required, SelectInput, SimpleFormIterator, TextInput, useNotify } from "react-admin";
import { useFormContext } from "react-hook-form";

// Types
import { UserMemberCreate } from "../../types/users";
import { FormMode } from "../../types/form-mode.enum";

// Constants
import { roles } from "../../constants/roles";

// Provider
import { Organization } from "../../providers/organizations";

// Hooks
import useValidators from "../../hooks/useValidators";

// Components
import { AddOrganization } from "./AddOrganization";
import DeleteMember from "../Organizations/DeleteMember";

type UserFormProps = {
  mode?: FormMode;
};

export const UserForm: React.FC<UserFormProps> = ({ mode }) => {
  const formContext = useFormContext();
  const notify = useNotify();
  const { text, email, role } = useValidators();

  const addOrg = (index: number) => {
    const organizations: Organization[] = formContext.getValues("organizations");
    const members = formContext.getValues("members");

    if (members.length > 0) {
      notify(`You can only assign a user to one organization at this time`, { type: "warning" });
      return;
    }

    const newListMembers = [{ organization: organizations[index], role: null }];
    const newOrganizations = organizations.filter((item) => item.id !== organizations[index].id);

    formContext.setValue("members", newListMembers, { shouldDirty: true });
    formContext.setValue("organizations", newOrganizations, { shouldDirty: true });
  };

  const deleteMember = () => {
    const DEFAULT_MEMBER_INDEX = 0;

    const organizations: Organization[] = formContext.getValues("organizations");
    const members: UserMemberCreate[] = formContext.getValues("members");

    const newOrganizations = [...organizations, members[DEFAULT_MEMBER_INDEX].organization];
    const newMembers = [] as UserMemberCreate[];

    formContext.setValue("members", newMembers, { shouldDirty: true });
    formContext.setValue("organizations", newOrganizations, { shouldDirty: true });
  };

  return (
    <>
      {mode === FormMode.EDIT && <TextInput label="Id" source="id" disabled />}
      <TextInput label="First name" source="first_name" validate={text} />
      <TextInput label="Last name" source="last_name" validate={text} />
      <TextInput label="Email" source="email" disabled={mode === FormMode.EDIT} validate={email} />
      <ArrayInput source="members">
        <SimpleFormIterator
          removeButton={<DeleteMember onClick={deleteMember} />}
          disableAdd
          disableReordering
          inline
          disableClear
        >
          <TextInput readOnly source="organization.name" helperText={false} />
          <SelectInput source="role" emptyText="No organization selected" choices={roles} validate={role} />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="organizations">
        <SimpleFormIterator
          removeButton={<AddOrganization onClick={addOrg} />}
          disableClear
          disableAdd
          disableReordering
          inline
        >
          <TextInput readOnly source="name" helperText={false} />
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};
